'use client';

import { faMessageLines } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { NoDataRow, SortableTableHeaderCell, Table, TableCell, TableHeaderCell, TableHeaderRow, TableRow } from '@pickleballinc/features/src/table';
import { Button, Tooltip } from '@pickleballinc/react-ui';
import { useQueryClient } from '@tanstack/react-query';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useState } from 'react';
import { toast } from 'react-toastify';

import { CaptainManagerDesignationForm } from '@/components/forms';
import { ApproveDeclineRequestModal } from '@/components/molecules/modals/ApproveDeclineRequestModal';
import { CaptainManagerDesignationData } from '@/entities';
import { CurrencyFormat } from '@/entities/CurrencyFormat';
import {
	ActivityPlayerInfoData,
	ApproveDeclineStatusEnum,
	PlayerTransferRequestType,
	TeamLeaguePlayerStatus,
	TeamPlayerInfo
} from '@/entities/TeamPlayerInfo';
import { ACTIVE_TEAMS_QUERY_KEY } from '@/hooks/constants';
import { useJoinRequest } from '@/hooks/hooks';
import { useUpdatePlayerRoleMutate } from '@/hooks/player/useUpdatePlayerRoleMutate';
import { useSortablePlayers } from '@/hooks/useSortablePlayers';
import { useOrganizationStore } from '@/store/organization-store';
import { DateUtil } from '@/utils/date';
import { getPlayerStatusInfo } from '@/utils/helpers';
import { determinePriceDisplay, getProfileImageUrl } from '@/utils/utils';

type TeamData = {
	name: string;
	teamId: string;
	players: TeamPlayerInfo[] | ActivityPlayerInfoData[];
};

interface PlayerTableProps {
	teamData: TeamData;
	handleModalOpen: (title: string) => void;
	handleSendMessageModalOpen: (title: string, groupOfPlayers: boolean, playerId?: string) => void;
	teamLeagueId: string;
	seasonId: string;
	ratings?: 'DUPR' | 'WPR';
	currency?: CurrencyFormat | null;
}

export const getRequestTypeValue = (type: PlayerTransferRequestType) => {
	switch (type) {
		case PlayerTransferRequestType.ADD:
			return 'Add';
		case PlayerTransferRequestType.JOIN:
			return 'Join';
		case PlayerTransferRequestType.TRADE:
			return 'Trade';
		case PlayerTransferRequestType.MOVE:
			return 'Move';
		case PlayerTransferRequestType.DELETE:
			return 'Drop';
		case PlayerTransferRequestType.REPLACE:
			return 'Replace';
		default:
			throw new Error('Invalid PlayerTransferRequestType');
	}
};

export const getRequestTypeValueInPast = (type: PlayerTransferRequestType) => {
	switch (type) {
		case PlayerTransferRequestType.ADD:
			return 'Added';
		case PlayerTransferRequestType.JOIN:
			return 'Joined';
		case PlayerTransferRequestType.TRADE:
			return 'Traded';
		case PlayerTransferRequestType.MOVE:
			return 'Moved';
		case PlayerTransferRequestType.DELETE:
			return 'Dropped';
		case PlayerTransferRequestType.REPLACE:
			return 'Replaced';
		default:
			throw new Error('Invalid PlayerTransferRequestType');
	}
};

export function TeamPlayersTable({ teamData, handleSendMessageModalOpen, teamLeagueId, seasonId, ratings = 'DUPR', currency }: PlayerTableProps) {
	const store = useOrganizationStore();
	const tlTimeZone = store.teamLeagueTimeZone;
	const { mutateAsync: joinRequest } = useJoinRequest();
	const [isDesignationModalOpen, setDesignationModalOpen] = useState(false);
	const path = usePathname();
	const isPlayerActivity = path.includes('player-activity');
	const { sortedPlayers, requestSort, sortConfig } = useSortablePlayers(teamData.players);

	const updatePlayerRoleMutation = useUpdatePlayerRoleMutate();
	const [selectedPlayerForEdit, setSelectedPlayerForEdit] = useState<TeamPlayerInfo | ActivityPlayerInfoData | null>(null);
	const [selectedPlayerForRequest, setSelectedPlayerForRequest] = useState<TeamPlayerInfo | ActivityPlayerInfoData | null>(null);
	const queryClient = useQueryClient();
	const isMLP = teamLeagueId === 'major-league-pickleball';

	const handleJoinRequest = async (tradeRequestUuid: string, action: string) => {
		try {
			await joinRequest({
				organizationId: store.organizationUuid,
				teamLeagueId: store.teamLeagueUuid,
				teamId: teamData.teamId,
				tradeRequestUuid,
				action
			});
			setSelectedPlayerForEdit(null);
		} catch (error) {
			toast.error('Something went wrong and we were unable to approve/decline player request.');
		}
	};

	const handlePlayerAction = (player: ActivityPlayerInfoData) => {
		setSelectedPlayerForEdit(player);
	};

	const handleDesignationSubmit = (playerId: string, data: CaptainManagerDesignationData) => {
		setDesignationModalOpen(false);

		const requestData = {
			organizationId: store.organizationUuid,
			teamLeagueId: store.teamLeagueUuid,
			teamLeagueTeamId: teamData.teamId,
			playerId,
			status: data.role,
			seasonId
		};

		updatePlayerRoleMutation.mutate(requestData, {
			onSuccess: () => {
				setDesignationModalOpen(false);

				queryClient.invalidateQueries({
					queryKey: [ACTIVE_TEAMS_QUERY_KEY, store.organizationUuid, teamLeagueId, seasonId]
				});
			}
		});
	};

	const handleEditClick = (player: TeamPlayerInfo) => {
		setSelectedPlayerForRequest(player);
		setDesignationModalOpen(true);
	};

	return (
		<>
			<Table>
				<thead className="bg-white">
					<TableHeaderRow>
						<SortableTableHeaderCell
							onSort={() => requestSort('fullname')}
							columnName="Player name"
							direction={sortConfig?.key === 'fullname' ? sortConfig.direction : undefined}
							className="w-8 text-center text-sm font-normal"
						>
							Player name
						</SortableTableHeaderCell>
						<SortableTableHeaderCell
							onSort={() => requestSort('ratingDoubles')}
							columnName={ratings}
							direction={sortConfig?.key === 'ratingDoubles' ? sortConfig.direction : undefined}
							className="w-8 text-center text-sm font-normal"
						>
							{ratings}
						</SortableTableHeaderCell>
						<SortableTableHeaderCell
							onSort={() => requestSort('designation')}
							columnName="Designation"
							direction={sortConfig?.key === 'designation' ? sortConfig.direction : undefined}
							className="w-8 text-center text-sm font-normal"
						>
							Status
						</SortableTableHeaderCell>
						<SortableTableHeaderCell
							onSort={() => requestSort('payment')}
							columnName="Payments"
							direction={sortConfig?.key === 'payment' ? sortConfig.direction : undefined}
							className="w-8 text-center text-sm font-normal"
						>
							Payments
						</SortableTableHeaderCell>
						{isPlayerActivity ? (
							<TableHeaderCell className="w-8 text-center text-sm font-normal">Actions</TableHeaderCell>
						) : (
							<>
								<SortableTableHeaderCell
									onSort={() => requestSort('availability')}
									columnName="Availability"
									direction={sortConfig?.key === 'availability' ? sortConfig.direction : undefined}
									className="w-8 text-center text-sm font-normal"
								>
									Availability
								</SortableTableHeaderCell>
								<SortableTableHeaderCell
									onSort={() => requestSort('status')}
									columnName="Captain/Manager"
									direction={sortConfig?.key === 'status' ? sortConfig.direction : undefined}
									className="w-8 text-center text-sm font-normal"
								>
									Captain/Co-captain
								</SortableTableHeaderCell>
								<TableHeaderCell className="w-8 text-center text-sm font-normal" />
							</>
						)}
					</TableHeaderRow>
				</thead>
				<tbody>
					{!teamData.players || teamData.players.length === 0 ? (
						<NoDataRow placeholder="No players available" />
					) : (
						(sortedPlayers as (TeamPlayerInfo | ActivityPlayerInfoData)[]).map((player, index) => (
							<TableRow
								key={`${(player as ActivityPlayerInfoData)?.transferRequestUuid}-${player.uuid}-${index}`}
								className={index % 2 === 0 ? 'bg-white' : 'bg-gray-100'}
							>
								<TableCell>
									<div className="flex gap-4">
										{isPlayerActivity ? (
											<img
												src={getProfileImageUrl((player as ActivityPlayerInfoData).photo?.profileImage?.url || '', 200, 200)}
												alt="avatar"
												className="size-12 rounded-full bg-gray-200 object-cover"
											/>
										) : (
											<img
												src={getProfileImageUrl((player as TeamPlayerInfo).thumbnail || '', 200, 200)}
												alt="avatar"
												className="size-12 rounded-full bg-gray-200 object-cover"
											/>
										)}

										<div className="!pr-16 lg:!pr-5">
											<Link
												href={`/team-leagues/${teamLeagueId}/season/${seasonId}/team/${teamData.teamId}/players/${player.playerUuid}`}
												className="text-sm font-medium text-blue-600 hover:underline text-nowrap"
											>
												{player.fullname}
											</Link>

											<div
												onClick={() =>
													player.sendTextAlerts && handleSendMessageModalOpen(player.fullname, false, player.uuid)
												}
											>
												{!player.sendTextAlerts ? (
													<Tooltip placement="bottom">
														<Tooltip.Trigger asChild>
															<p className="text-xs text-gray-400">{player.internationalCallingCode + player.phone}</p>
														</Tooltip.Trigger>
														<Tooltip.Content>
															<p>This player has opted out of SMS messages.</p>
														</Tooltip.Content>
													</Tooltip>
												) : (
													<div className="flex cursor-pointer items-center gap-2 text-darkorange-400">
														<p className="text-xs">{player.internationalCallingCode + player.phone}</p>
														<FontAwesomeIcon icon={faMessageLines} size="sm" />
													</div>
												)}
											</div>
										</div>
									</div>
								</TableCell>
								<TableCell className="flex justify-center items-center">
									<div className="text-left">
										<p className="text-sm font-medium">(D) {player.ratingDoubles?.toFixed(2) ?? 'NR'}</p>
										<p className="text-xs text-gray-500">(S) {player.ratingSingles?.toFixed(2) ?? 'NR'}</p>
										{!isMLP && <p className="text-xs text-gray-500">(SS) {player.skinnySingles?.toFixed(2) ?? 'NR'}</p>}
									</div>
								</TableCell>
								<TableCell className="text-center">
									{isPlayerActivity ? (
										(player as ActivityPlayerInfoData).isCompleted &&
										((player as ActivityPlayerInfoData).isApprovedByCommissioner ===
											ApproveDeclineStatusEnum.PLAYER_TRANSFER_APPROVAL_STATUS_DECLINED ||
											(player as ActivityPlayerInfoData).isApprovedByTeam ===
												ApproveDeclineStatusEnum.PLAYER_TRANSFER_APPROVAL_STATUS_DECLINED ||
											(player as ActivityPlayerInfoData).isApprovedByPlayer ===
												ApproveDeclineStatusEnum.PLAYER_TRANSFER_APPROVAL_STATUS_DECLINED) ? (
											<div className="flex items-center justify-center">
												<div className="size-1.5 rounded-full bg-error-500 shrink-0 mr-2" />
												<span className="text-xs font-medium">{`Declined - ${getRequestTypeValue((player as ActivityPlayerInfoData).transferType)}`}</span>
											</div>
										) : (player as ActivityPlayerInfoData).isCompleted &&
										  ((player as ActivityPlayerInfoData).isApprovedByTeam ===
												ApproveDeclineStatusEnum.PLAYER_TRANSFER_APPROVAL_STATUS_APPROVED ||
												(player as ActivityPlayerInfoData).isApprovedByPlayer ===
													ApproveDeclineStatusEnum.PLAYER_TRANSFER_APPROVAL_STATUS_APPROVED) ? (
											<div className="flex items-center justify-center">
												<div className="size-1.5 rounded-full bg-green-500 shrink-0 mr-2" />
												<span className="text-xs font-medium">{`Done - ${getRequestTypeValueInPast((player as ActivityPlayerInfoData).transferType)}`}</span>
											</div>
										) : (
											<div className="flex items-center justify-center">
												<div className="size-1.5 rounded-full bg-darkorange-300 shrink-0 mr-2" />
												<span className="text-xs font-medium">{`Pending - Player must approve ${getRequestTypeValue((player as ActivityPlayerInfoData).transferType)}`}</span>
											</div>
										)
									) : (
										<div>
											{(player as TeamPlayerInfo).isInjured ? (
												<div className="flex items-center justify-center">
													<div className="size-1.5 rounded-full bg-error-500 shrink-0 mr-2" />
													<span className="text-xs font-medium">Injured Reserve</span>
												</div>
											) : (
												<div className="flex items-center justify-center">
													<div className="size-1.5 rounded-full bg-green-500 shrink-0 mr-2" />
													<span className="text-xs font-medium">Active</span>
												</div>
											)}
										</div>
									)}
								</TableCell>
								<TableCell className="text-center">
									<p className="text-xs">
										{currency ? determinePriceDisplay(player.payment, currency) : player.payment?.toFixed(2)}
									</p>
								</TableCell>
								{isPlayerActivity ? (
									<TableCell className="text-center">
										{!(player as ActivityPlayerInfoData)?.isCompleted ? (
											(player as ActivityPlayerInfoData)?.isApprovedByCommissioner ===
											ApproveDeclineStatusEnum.PLAYER_TRANSFER_APPROVAL_STATUS_PENDING ? (
												<Tooltip placement="bottom">
													<Tooltip.Trigger asChild>
														<Button
															onClick={() => handlePlayerAction(player as ActivityPlayerInfoData)}
															variant="secondary"
															size="xs"
															disabled
														>
															Approve/Decline
														</Button>
													</Tooltip.Trigger>
													<Tooltip.Content className="z-[100]">
														<p className="text-center">
															The commissioner must first approve <br /> the request on the manage side.
														</p>
													</Tooltip.Content>
												</Tooltip>
											) : (
												<Button
													onClick={() => handlePlayerAction(player as ActivityPlayerInfoData)}
													variant="secondary"
													size="xs"
												>
													Approve/Decline
												</Button>
											)
										) : (
											<div className="text-xs font-normal leading-[18px] text-gray-900 flex flex-col items-center">
												<div>
													<p className="text-left text-nowrap">
														{`Effective ` +
															DateUtil.formatUTCInTimezone(
																(player as ActivityPlayerInfoData)?.transferRequestDateUpdated ??
																	(player as ActivityPlayerInfoData).transferRequestDateCreated,
																'MMM DD, YYYY',
																tlTimeZone?.ianaTzIdentifier
															)}
													</p>
													<p className="text-left text-nowrap">
														{DateUtil.formatUTCInTimezone(
															(player as ActivityPlayerInfoData)?.transferRequestDateUpdated ??
																(player as ActivityPlayerInfoData).transferRequestDateCreated,
															'hh:mm A',
															tlTimeZone?.ianaTzIdentifier
														)}
													</p>
												</div>
											</div>
										)}
									</TableCell>
								) : (
									<>
										<TableCell className="text-center">
											<p className="text-xs">{(player as TeamPlayerInfo).availability}</p>
										</TableCell>
										<TableCell className="text-center">
											{(() => {
												const { label, className } = getPlayerStatusInfo(
													(player as TeamPlayerInfo).status as TeamLeaguePlayerStatus
												);
												return label ? <p className={`text-xs font-medium ${className}`}>{label}</p> : null;
											})()}
										</TableCell>
										<TableCell className="text-center">
											<button
												onClick={() => handleEditClick(player as TeamPlayerInfo)}
												className="rounded bg-blue-100 px-3 py-1 text-xs font-medium text-blue-600 hover:bg-blue-200"
											>
												Edit
											</button>
										</TableCell>
									</>
								)}
							</TableRow>
						))
					)}
				</tbody>
			</Table>
			{selectedPlayerForRequest && isDesignationModalOpen ? (
				<CaptainManagerDesignationForm
					key={`${teamData.teamId}-${selectedPlayerForRequest.playerUuid}`}
					title="Designate captains / co-captains"
					teamName={teamData.name}
					onClose={() => setDesignationModalOpen(false)}
					onSubmit={(formData) => handleDesignationSubmit(selectedPlayerForRequest.playerUuid!, formData)}
					playerStatus={selectedPlayerForRequest.status as TeamLeaguePlayerStatus}
				/>
			) : null}

			{selectedPlayerForEdit ? (
				<ApproveDeclineRequestModal
					fullName={selectedPlayerForEdit.fullname}
					teamName={teamData.name}
					onSubmit={(action) => handleJoinRequest((selectedPlayerForEdit as ActivityPlayerInfoData).transferRequestUuid, action)}
					onClose={() => setSelectedPlayerForEdit(null)}
				/>
			) : null}
		</>
	);
}
